import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  apiClient,
  handleApiError,
  handleApiSuccess,
} from "@finitless/shared/lib";
import { useToast } from "@finitless/shared/hooks";

export const AVAILABLE_LOCALES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "ar", label: "Arabic" },
];

/**
 * Extract placeholders like {order_id} or {{type}} from text
 * @param {string} text
 * @returns {string[]}
 */
export const extractPlaceholders = (text) => {
  if (!text) return [];
  const matches = text.match(/\{\{[^{}]+\}\}|\{[^{}]+\}/g);
  return matches ? [...new Set(matches)] : [];
};

/**
 * Hook to manage AI Agent translations
 */
export const useAgentTranslations = () => {
  const { t } = useTranslation("agentTranslations");
  const toast = useToast();
  const queryClient = useQueryClient();

  /**
   * Query to get translations for an agent by locale
   */
  const useTranslations = (agentId, locale = "en") => {
    const query = useQuery({
      queryKey: ["agent-translations", agentId, locale],
      queryFn: async () => {
        const response = await apiClient.get(
          `/ai/agents/${agentId}/translations?locale=${locale}`,
        );
        return response?.result || {};
      },
      enabled: !!agentId && AVAILABLE_LOCALES.some((l) => l.code === locale),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    });

    useEffect(() => {
      if (!query.error) {
        return;
      }
      handleApiError(query.error, toast, {
        title: t("toasts.translationsErrorTitle"),
      });
    }, [query.error, t, toast]);

    return query;
  };

  /**
   * Mutation to update translations
   * @example
   * updateTranslations.mutate({
   *   agentId: 123,
   *   translations: {
   *     en: { welcome_message: "Hello!", goodbye_message: "Bye!" },
   *     es: { welcome_message: "Hola!" }
   *   }
   * })
   */
  const useUpdateTranslations = () => {
    return useMutation({
      mutationFn: async ({ agentId, translations }) => {
        const response = await apiClient.put(
          `/ai/agents/${agentId}/translations`,
          { translations },
        );
        return response?.result;
      },
      onSuccess: (_data, variables) => {
        // Optimistically update cache so character counts don't flash old values
        const { agentId, translations: updatedTranslations } = variables;
        for (const [locale, values] of Object.entries(updatedTranslations)) {
          queryClient.setQueryData(
            ["agent-translations", agentId, locale],
            (old) => {
              if (!old) return old;
              const updated = { ...old };
              for (const [key, value] of Object.entries(values)) {
                if (updated[key]) {
                  updated[key] = { ...updated[key], value, isCustom: true };
                }
              }
              return updated;
            },
          );
        }
        // Then invalidate to get fresh data from server
        queryClient.invalidateQueries({
          queryKey: ["agent-translations", variables.agentId],
        });
        handleApiSuccess(t("toasts.updateSuccess"), toast, {
          title: t("toasts.successTitle"),
        });
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("toasts.translationUpdateErrorTitle"),
          defaultMessage: t("toasts.translationUpdateErrorMessage"),
        });
      },
    });
  };

  /**
   * Mutation to copy translations from another agent
   * Both agents must have the same front_type
   */
  const useCopyTranslations = () => {
    return useMutation({
      mutationFn: async ({ targetAgentId, sourceAgentId }) => {
        const response = await apiClient.post(
          `/ai/agents/${targetAgentId}/translations/copy`,
          { source_agent_id: sourceAgentId },
        );
        return response?.result;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["agent-translations", variables.targetAgentId],
        });
        handleApiSuccess(t("toasts.copySuccess"), toast, {
          title: t("toasts.successTitle"),
        });
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("toasts.copyErrorTitle"),
          defaultMessage: t("toasts.copyErrorMessage"),
        });
      },
    });
  };

  /**
   * Mutation to reset a single translation to default
   * Sends null for the specific key to remove customization
   */
  const useResetTranslation = () => {
    return useMutation({
      mutationFn: async ({ agentId, locale, key }) => {
        const translations = {
          [locale]: { [key]: null },
        };
        const response = await apiClient.put(
          `/ai/agents/${agentId}/translations`,
          { translations },
        );
        return response?.result;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["agent-translations", variables.agentId, variables.locale],
        });
        handleApiSuccess(t("toasts.resetSuccess"), toast, {
          title: t("toasts.successTitle"),
        });
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("toasts.resetErrorTitle"),
          defaultMessage: t("toasts.resetErrorMessage"),
        });
      },
    });
  };

  return {
    useTranslations,
    useUpdateTranslations,
    useCopyTranslations,
    useResetTranslation,
  };
};

export default useAgentTranslations;
